import React from "react";
import {
  FileText,
  FileDown,
  FileCode2,
  CheckCircle2,
  Sparkles,
  ShieldCheck,
  Workflow,
  Users,
  Clock,
  Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

const FILES = [
  {
    label: "Full PRD (PDF)",
    desc: "Print-ready, branded — ideal for sharing with stakeholders, auditors, investors.",
    href: "/downloads/ImmiPro_PRD_v2.0.pdf",
    size: "170 KB",
    icon: FileText,
    primary: true,
  },
  {
    label: "Editable PRD (DOCX)",
    desc: "Open in Word / Google Docs to customise scope, add features, redline.",
    href: "/downloads/ImmiPro_PRD_v2.0.docx",
    size: "38 KB",
    icon: FileDown,
    primary: false,
  },
  {
    label: "Source Markdown (MD)",
    desc: "Plain-text source — version-control friendly, paste into Notion/Confluence.",
    href: "/downloads/ImmiPro_PRD_v2.0.md",
    size: "45 KB",
    icon: FileCode2,
    primary: false,
  },
];

const TOC = [
  "Executive Summary",
  "Vision, Strategy & Positioning",
  "Market Research — 20 Gaps & Bottlenecks",
  "Personas & Jobs-to-be-Done",
  "Functional Scope — 16 Modules",
  "User Stories & Acceptance Criteria",
  "Non-Functional Requirements",
  "Compliance & Regulatory Map",
  "Visual Identity & IP Clean-Room",
  "Technical Architecture",
  "Complete API Catalogue (Internal + External)",
  "Data Model & Schemas",
  "Solo-Builder Build Plan & Timeline",
  "Phased Roadmap (Phase 0 → Phase 3)",
  "Pricing & Monetisation Model",
  "Go-to-Market & Launch Plan",
  "Risks, Assumptions & Mitigations",
  "KPIs & Success Metrics",
  "Extensibility & Customisation Guide",
  "Glossary",
  "Appendix — Sources & References",
];

const HIGHLIGHTS = [
  { icon: Workflow, title: "16 Modules", body: "End-to-end ERP scope from marketing site to public API + webhooks." },
  { icon: Sparkles, title: "AI Copilot Native", body: "Pathway, RFI, classifier, triage, policy pulse — not a bolt-on." },
  { icon: ShieldCheck, title: "OMARA & ISO 27001", body: "Compliance map for every regulation that touches an Australian RMA." },
  { icon: Clock, title: "14-week Solo Build", body: "Sprint plan, hour estimates, AUD cost breakdown — for a single founder." },
  { icon: Wallet, title: "Tiered Pricing", body: "Solo Starter at AUD $49/mo opens the funnel MM closes." },
  { icon: Users, title: "5 Personas", body: "Solo RMA → Big-4 mobility → applicant — every JTBD mapped to a module." },
];

const DIFFERENTIATORS = [
  "100% web-native — no desktop install or hybrid VM (vs MM eLodge).",
  "Runtime dynamic state-machine workflows — checklists mutate on ART, BVE, sponsor changes.",
  "Timeline client portal in 12 languages — not a file dump.",
  "Policy Pulse — DHA/OMARA/LegEnd updates auto-flag impacted matters.",
  "Solo-friendly pricing tier — removes the cost barrier for new practitioners.",
];

export default function DownloadPage() {
  const handleDownload = (file) => {
    // Trigger download via anchor click
    const link = document.createElement("a");
    link.href = file.href;
    link.download = file.href.split("/").pop();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-[#F4F7FA] text-[#0A0A0A]">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
          <div className="flex items-center gap-3">
            <img src="/immipro-logo.png" alt="ImmiPro" className="h-8 w-auto" /><div className="sr-only"><div>
                ImmiPro
              </div>
              <div className="text-xs uppercase tracking-widest text-slate-500">
                Product Requirements Document
              </div>
            </div>
          </div>
          <Badge variant="secondary" className="bg-[#2ECC9A]/10 text-[#2ECC9A] border-[#2ECC9A]/30">
            v2.0 — Full Edition
          </Badge>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-white to-[#F4F7FA]">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <div className="grid gap-12 md:grid-cols-5">
            <div className="md:col-span-3">
              <div className="text-xs font-semibold uppercase tracking-[0.25em] text-[#2ECC9A]">
                Build-ready · Customisable · Confidential
              </div>
              <h1 className="mt-3 font-serif text-4xl font-bold leading-tight tracking-tight text-[#1A3A5C] md:text-5xl">
                Next-Generation Immigration Practice ERP for Australia
              </h1>
              <p className="mt-5 max-w-2xl text-lg text-slate-600">
                A full-edition PRD covering 16 modules, complete API catalogue, data model, 14-week solo build plan with AUD cost breakdown, pricing tiers, GTM, KPIs, and extensibility hooks. Designed to be customised, extended, and signed off.
              </p>

              {/* Download buttons */}
              <div className="mt-8 flex flex-wrap gap-3">
                {FILES.map((f) => {
                  const Icon = f.icon;
                  return (
                    <Button
                      key={f.label}
                      onClick={() => handleDownload(f)}
                      variant={f.primary ? "default" : "outline"}
                      className={
                        f.primary
                          ? "h-12 gap-2 bg-[#1A3A5C] px-6 text-white hover:bg-[#1A3A5C]/90"
                          : "h-12 gap-2 border-[#1A3A5C]/20 px-6 text-[#1A3A5C] hover:bg-[#1A3A5C]/5"
                      }
                    >
                      <Icon className="h-4 w-4" />
                      {f.label}
                      <span className="ml-1 text-xs opacity-70">{f.size}</span>
                    </Button>
                  );
                })}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500">
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-[#2ECC9A]" />
                  Clean-room IP compliant
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-[#2ECC9A]" />
                  21 sections, ~46 pages
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-[#2ECC9A]" />
                  OMARA + APP + ISO 27001 mapped
                </span>
              </div>
            </div>

            {/* Doc card */}
            <div className="md:col-span-2">
              <Card className="overflow-hidden border-slate-200 shadow-xl">
                <div className="bg-[#1A3A5C] p-6 text-white">
                  <div className="text-xs uppercase tracking-widest text-[#2ECC9A]">
                    Document Control
                  </div>
                  <div className="mt-2 text-sm">ImmiPro PRD v2.0</div>
                </div>
                <CardContent className="p-6">
                  <dl className="space-y-3 text-sm">
                    <div className="flex justify-between border-b border-slate-100 pb-2">
                      <dt className="text-slate-500">Version</dt>
                      <dd className="font-medium">2.0 — Full Edition</dd>
                    </div>
                    <div className="flex justify-between border-b border-slate-100 pb-2">
                      <dt className="text-slate-500">Date</dt>
                      <dd className="font-medium">July 2025</dd>
                    </div>
                    <div className="flex justify-between border-b border-slate-100 pb-2">
                      <dt className="text-slate-500">Benchmark</dt>
                      <dd className="font-medium">Migration Manager AU</dd>
                    </div>
                    <div className="flex justify-between border-b border-slate-100 pb-2">
                      <dt className="text-slate-500">Compliance</dt>
                      <dd className="font-medium">OMARA · APP · ISO 27001</dd>
                    </div>
                    <div className="flex justify-between border-b border-slate-100 pb-2">
                      <dt className="text-slate-500">Build effort</dt>
                      <dd className="font-medium">~515 hrs · 14 wks solo</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-slate-500">MVP cloud cost</dt>
                      <dd className="font-medium">~AUD $2,439 (3 mo)</dd>
                    </div>
                  </dl>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="mx-auto max-w-6xl px-6 py-16">
        <div className="text-xs font-semibold uppercase tracking-[0.25em] text-[#2ECC9A]">
          What's inside
        </div>
        <h2 className="mt-2 font-serif text-3xl font-bold tracking-tight text-[#1A3A5C]">
          A blueprint, not just a brief.
        </h2>
        <p className="mt-3 max-w-2xl text-slate-600">
          Every section is structured so a solo founder (or a team) can pick a module, scope it, hand to engineering, and ship — without re-deriving requirements from scratch.
        </p>

        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {HIGHLIGHTS.map((h) => {
            const Icon = h.icon;
            return (
              <Card key={h.title} className="border-slate-200 transition-shadow hover:shadow-md">
                <CardContent className="p-6">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#2ECC9A]/10">
                    <Icon className="h-5 w-5 text-[#2ECC9A]" />
                  </div>
                  <div className="mt-4 font-semibold text-[#1A3A5C]">{h.title}</div>
                  <div className="mt-1 text-sm text-slate-600">{h.body}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Differentiators */}
      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <div className="grid gap-12 md:grid-cols-2">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.25em] text-[#2ECC9A]">
                Why ImmiPro wins
              </div>
              <h2 className="mt-2 font-serif text-3xl font-bold tracking-tight text-[#1A3A5C]">
                Five things only ImmiPro does.
              </h2>
              <p className="mt-3 text-slate-600">
                Each differentiator is mapped to a specific gap in the incumbent (Migration Manager) — backed by user reviews, market research, and the BRD's clean-room scope.
              </p>
            </div>
            <ul className="space-y-3">
              {DIFFERENTIATORS.map((d, i) => (
                <li key={i} className="flex gap-3 rounded-md border border-slate-200 bg-[#F4F7FA] p-4">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#1A3A5C] text-xs font-bold text-[#2ECC9A]">
                    {i + 1}
                  </div>
                  <span className="text-sm text-[#1A3A5C]">{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* TOC */}
      <section className="mx-auto max-w-6xl px-6 py-16">
        <div className="text-xs font-semibold uppercase tracking-[0.25em] text-[#2ECC9A]">
          Table of contents
        </div>
        <h2 className="mt-2 font-serif text-3xl font-bold tracking-tight text-[#1A3A5C]">
          21 sections, end-to-end.
        </h2>

        <div className="mt-10 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {TOC.map((s, i) => (
            <div
              key={s}
              className="flex items-center gap-3 rounded-md border border-slate-200 bg-white p-4 transition-colors hover:border-[#2ECC9A]"
            >
              <span className="font-mono text-xs text-slate-400">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-sm font-medium text-[#1A3A5C]">{s}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#1A3A5C] text-white">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <div className="grid gap-8 md:grid-cols-2 md:items-center">
            <div>
              <h2 className="font-serif text-3xl font-bold tracking-tight">
                Ready to customise & extend?
              </h2>
              <p className="mt-3 max-w-xl text-slate-300">
                Grab the editable copies. Add features, redline scope, slot in new modules using the Extensibility Guide in §19 — then come back and we'll start building Phase 0.
              </p>
            </div>
            <div className="flex flex-col gap-3 md:items-end">
              {FILES.map((f) => {
                const Icon = f.icon;
                return (
                  <Button
                    key={f.label}
                    onClick={() => handleDownload(f)}
                    variant="outline"
                    className="h-11 w-full justify-start gap-2 border-white/20 bg-white/5 text-white hover:bg-[#2ECC9A] hover:text-[#1A3A5C] hover:border-[#2ECC9A] md:w-80"
                  >
                    <Icon className="h-4 w-4" />
                    {f.label}
                    <span className="ml-auto text-xs opacity-70">{f.size}</span>
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-[#F4F7FA]">
        <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-4 px-6 py-8 md:flex-row md:items-center">
          <div className="text-sm text-slate-500">
            © 2025 ImmiPro — Confidential, Commercial. Clean-room blueprint.
          </div>
          <div className="text-xs uppercase tracking-widest text-slate-400">
            Built compliance-first · designed client-first · priced practice-first
          </div>
        </div>
      </footer>
    </div>
  );
}
